import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Link } from "@tanstack/react-router";
import { castForumVote, myForumVotes } from "@/lib/forum.functions";
import { useAuth } from "@/hooks/use-auth";

export function useMyVotes() {
  const { signedIn } = useAuth();
  const fn = useServerFn(myForumVotes);
  return useQuery({
    queryKey: ["forum-my-votes"],
    queryFn: () => fn({ data: {} }),
    enabled: signedIn,
    staleTime: 30_000,
  });
}

type Props = {
  postId?: string;
  commentId?: string;
  score: number;
  layout?: "column" | "row";
};

export function VoteButtons({ postId, commentId, score, layout = "column" }: Props) {
  const { signedIn } = useAuth();
  const vote = useServerFn(castForumVote);
  const qc = useQueryClient();
  const { data: mine } = useMyVotes();
  const [pending, setPending] = useState(false);
  const [override, setOverride] = useState<{ value: number; score: number } | null>(null);

  const serverValue = useMemo(() => {
    if (!mine) return 0;
    if (postId) return mine.posts?.[postId] ?? 0;
    if (commentId) return mine.comments?.[commentId] ?? 0;
    return 0;
  }, [mine, postId, commentId]);

  const current = override?.value ?? serverValue;
  const shown = override?.score ?? score;

  async function press(next: 1 | -1) {
    if (!signedIn) {
      toast.info("Logga in för att rösta");
      return;
    }
    const value = current === next ? 0 : next;
    const delta = value - current;
    setOverride({ value, score: shown + delta });
    setPending(true);
    try {
      await vote({ data: { post_id: postId ?? null, comment_id: commentId ?? null, value } });
      qc.invalidateQueries({ queryKey: ["forum-my-votes"] });
      qc.invalidateQueries({ queryKey: ["forum"] });
    } catch (err) {
      setOverride(null);
      toast.error(err instanceof Error ? err.message : "Kunde inte rösta");
    } finally {
      setPending(false);
    }
  }

  const wrap =
    layout === "column"
      ? "flex flex-col items-center gap-0.5"
      : "inline-flex items-center gap-1";

  const btn = "grid h-7 w-7 place-items-center rounded-lg transition disabled:opacity-50";

  return (
    <div className={wrap}>
      <button
        type="button"
        onClick={() => press(1)}
        disabled={pending}
        aria-label="Rösta upp"
        className={`${btn} ${current === 1 ? "bg-accent/15 text-accent" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}
      >
        <ChevronUp size={16} />
      </button>
      <span
        className={`min-w-[1.5rem] text-center text-xs font-bold tabular-nums ${
          current === 1 ? "text-accent" : current === -1 ? "text-muted-foreground" : "text-foreground"
        }`}
      >
        {shown}
      </span>
      <button
        type="button"
        onClick={() => press(-1)}
        disabled={pending}
        aria-label="Rösta ner"
        className={`${btn} ${current === -1 ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}
      >
        <ChevronDown size={16} />
      </button>
    </div>
  );
}

export function SignInHint({ what }: { what: string }) {
  return (
    <p className="rounded-2xl border border-border bg-card p-4 text-sm">
      <Link to="/login" className="text-accent hover:underline">
        Logga in
      </Link>{" "}
      för att {what}.
    </p>
  );
}
